import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, User, GoogleAuthProvider, signInWithPopup, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isFullyAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  loginWithGoogle: () => Promise<User>;
  logout: () => Promise<void>;
  setFullyAuthenticated: (value: boolean) => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Create a context with a default value that matches the shape of AuthContextType but is undefined
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create a loading component to avoid inline JSX in the provider
const LoadingSpinner: React.FC = () => (
    <div className="loading-fallback">
      <div className="loading-content">
        <div className="loading-spinner"></div>
        <div className="loading-text">
          <p>Loading...</p>
        </div>
      </div>
    </div>
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const {t} = useTranslation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isFullyAuthenticated, setFullyAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsLoading(false);
    });

    // Clean up subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  // Use useCallback to memoize functions that are passed down as props
  const login = useCallback(async (email: string, password: string): Promise<User> => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Login failed: ${errorMessage}`);
      throw error;
    }
  }, [auth]);

  const register = useCallback(async (email: string, password: string): Promise<User> => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Registration failed: ${errorMessage}`);
      throw error;
    }
  }, [auth]);

  // Separate the navigation logic from auth logic
  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await signOut(auth);
      // Reset fully authenticated state
      setFullyAuthenticated(false);
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Logout failed: ${errorMessage}`);
      throw error;
    }
  }, [auth, setFullyAuthenticated]);

  const logout = useCallback(async (): Promise<void> => {
    await handleLogout();
    // Navigate after logout
    navigate('/signin');
  }, [handleLogout, navigate]);

  // Google sign-in method
  const loginWithGoogle = useCallback(async (): Promise<User> => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Google login failed: ${errorMessage}`);
      throw error;
    }
  }, [auth]);

  // Change password function
  const changePassword = useCallback(async (currentPassword: string, newPassword: string): Promise<void> => {
    if (!currentUser || !currentUser.email) {
      toast.error(t('No authenticated user found'));
      throw new Error('No authenticated user found');
    }

    try {
      // Create credential with current password
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );

      // Reauthenticate user
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      toast.success(t('Password updated successfully'));
    } catch (error: any) {
      console.error('Password change error:', error);

      // Handle specific error codes
      if (error.code === 'auth/wrong-password') {
        toast.error(t('Current password is incorrect'));
      } else if (error.code === 'auth/weak-password') {
        toast.error(t('New password is too weak'));
      } else {
        toast.error(t('Failed to update password'));
      }

      throw error;
    }
  }, [currentUser, t]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    currentUser,
    isAuthenticated: !!currentUser,
    isFullyAuthenticated,
    isLoading,
    login,
    register,
    loginWithGoogle,
    logout,
    setFullyAuthenticated,
    changePassword,
  }), [currentUser, isFullyAuthenticated, isLoading, login, register, loginWithGoogle, logout, setFullyAuthenticated, changePassword]);

  // Show loading state while auth is initializing
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};